/**
 * Limite de tamanho e formatos de IMAGEM aceitos pela Cloud API do WhatsApp.
 *
 * Mesma razão de ser de `utils/video/limiteVideoMeta.ts`: o envio de mídia para
 * a Meta é por LINK (`{ image: { link } }`). O whiz-server nunca toca nos bytes,
 * a Graph responde `200` com um `wamid`, e a recusa só chega depois, por
 * webhook (`131053 Media upload error`). Para o operador é uma imagem que
 * "sumiu". Validar e ajustar aqui, antes do upload, é o que faz ela chegar.
 *
 * Regras da Cloud API para `image`:
 * - formatos: `image/jpeg` e `image/png` — webp/gif/heic/bmp/avif são recusados;
 * - tamanho: até 5 MB.
 *
 * Só `WHATSAPP_META` entra aqui. Instagram e Messenger têm outras regras, e o
 * inbox híbrido decide o transporte no servidor — mandar uma imagem que a
 * Evolution aceitaria intacta por uma conversão à toa seria pior que não mexer.
 */

const MB = 1024 * 1024;

export type PlataformaComLimiteDeImagem = 'WHATSAPP_META';

export interface RegrasDeImagem {
    limiteBytes: number;
    /** Content-types aceitos, já normalizados (minúsculo, sem parâmetros). */
    formatosAceitos: readonly string[];
}

export const REGRAS_DE_IMAGEM_POR_PLATAFORMA: Readonly<Record<PlataformaComLimiteDeImagem, RegrasDeImagem>> = {
    WHATSAPP_META: {
        limiteBytes: 5 * MB,
        formatosAceitos: ['image/jpeg', 'image/png'],
    },
};

/** Regras do canal, ou `null` quando o canal não tem restrição de imagem aqui. */
export function regrasDeImagem(plataforma?: string | null): RegrasDeImagem | null {
    return plataforma === 'WHATSAPP_META' ? REGRAS_DE_IMAGEM_POR_PLATAFORMA[plataforma] : null;
}

/** `"image/JPG; foo=bar"` → `"image/jpeg"`. */
export function normalizarMimeDeImagem(mime?: string | null): string {
    const base = mime?.split(';').at(0)?.trim().toLowerCase() ?? '';
    return base === 'image/jpg' || base === 'image/pjpeg' ? 'image/jpeg' : base;
}

export function formatoDeImagemAceito(regras: RegrasDeImagem, mime?: string | null): boolean {
    return regras.formatosAceitos.includes(normalizarMimeDeImagem(mime));
}

/**
 * Formatos de origem SEM perda. Convertê-los para PNG preserva cada pixel, então
 * é a primeira tentativa quando o resultado couber; os demais (webp, avif, heic)
 * já perderam informação na origem e vão direto para JPEG.
 */
const FORMATOS_SEM_PERDA = ['image/png', 'image/gif', 'image/bmp', 'image/x-ms-bmp', 'image/tiff', 'image/svg+xml'];

export function formatoSemPerda(mime?: string | null): boolean {
    return FORMATOS_SEM_PERDA.includes(normalizarMimeDeImagem(mime));
}
